/**
 * The document AI layer's adapter (slate-feature-transfer W8 §C, §D.3).
 *
 * ONE namespace over TWO controllers. Every method below names its face, because they do not
 * agree and the difference is load-bearing:
 *
 *  - **turn face** `api/tomatoflow/knowit/thoughts` — messages, SSE streaming, the context
 *    pack, assigned thinkers, prompts, outcomes/BDI, commit/reopen/regenerate/flatten. Its
 *    message projection is RAW: the control rows (`context_item`, `derived_artifact`,
 *    `conversation_status`, …) come back as messages, which is exactly why the drawer reads
 *    this face and filters client-side (§F.4 decision 2).
 *  - **lifecycle face** `api/document-conversations` — list/rename/delete+restore/archive/
 *    merge/transform/summary/rethink. Its projection HIDES the twelve `NonTurnMessageTypes`.
 *
 * Everything is bare (§0.2) and rides `rest()`, which is 204-tolerant — `DELETE .../context/{id}`
 * is the case that proves it. There are NO abilities anywhere on this wire (§0.3): the caller
 * gates on the host document's own policy and never derives one from a conversation payload.
 *
 * ⚠ W8-6 (server, CRITICAL): `GET .../thoughts/document/{id}` and `/stats` check the document's
 * existence with `.IgnoreQueryFilters()`, so a foreign document answers 200. The client cannot
 * fix that and must not widen it — never send a document id the viewer has not opened.
 */

import type { ConversationStreamHandler } from "~/api/conversationStream";
import {
  type BdiRun,
  BdiRunSchema,
  type BindDraft,
  type CommitResult,
  type CompanionResult,
  type ContextConfig,
  ContextConfigSchema,
  type ContextItem,
  type ContextItemDraft,
  ContextItemSchema,
  type ContextPack,
  ContextPackSchema,
  type ContextSnapshot,
  ContextSnapshotSchema,
  type Conversation,
  type ConversationMessage,
  type ConversationSearch,
  type ConversationStats,
  type ConversationSummary,
  type ConversationsOverview,
  type Decision,
  type DecisionAction,
  type DecisionDraft,
  DecisionSchema,
  type DerivedTruth,
  DerivedTruthSchema,
  type DocumentAiSettings,
  DocumentAiSettingsSchema,
  type DocumentThinkerResolution,
  DocumentThinkerResolutionSchema,
  type ExecutiveDigestContent,
  ExecutiveDigestContentSchema,
  type FlattenResult,
  type MessageDraft,
  type Outcomes,
  OutcomesSchema,
  type PlanDraft,
  type PromptRunDraft,
  parseConversation,
  parseLifecycleSummary,
  parseMessage,
  parseTurnSummary,
  priorityValue,
  type RegenerateResult,
  type RethinkResult,
  type RevisePatch,
  type ReviseResult,
  type SourceTrace,
  SourceTraceSchema,
  type StartConversationDraft,
  type ThinkerDisposition,
  type ThinkerRemovalResult,
  type TruthDraft,
  type UsageRollup,
  UsageRollupSchema,
} from "~/api/conversationTypes";
import { ApiError } from "~/api/errors";

/** What this module needs from the class that owns the bearer. */
export interface ConversationTransport {
  /** 204-tolerant REST over `/api/…`, bare bodies, the server's own words on 403/404. */
  rest(path: string, method: string, body?: Record<string, unknown>): Promise<unknown>;
  /** The `text/event-stream` lane, `event:`-framed. */
  stream(
    path: string,
    body: Record<string, unknown>,
    onEvent: ConversationStreamHandler,
    signal?: AbortSignal,
  ): Promise<void>;
}

const TURN = "tomatoflow/knowit/thoughts";
const LIFECYCLE = "document-conversations";

const enc = encodeURIComponent;

const obj = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
const arr = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);
const str = (value: unknown): string | null => (typeof value === "string" ? value : null);
const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === "string") : [];
const int = (value: unknown, fallback = 0): number =>
  typeof value === "number" ? value : fallback;

/**
 * W8-7 — every `DocumentThoughtsController` action is wrapped in
 * `catch(ex) → 500 {"error": ex.Message}`, so a stack-trace fragment or an SQL error text can
 * reach the browser. It is logged and replaced here; rendering it would be both ugly and a
 * small information leak.
 */
async function call<T>(work: Promise<unknown>, map: (raw: unknown) => T): Promise<T> {
  try {
    return map(await work);
  } catch (error) {
    if (error instanceof ApiError && error.status >= 500) {
      console.warn("[conversations] server error", error.status, error.message);
      throw new ApiError("The conversation service could not answer", error.status);
    }
    throw error;
  }
}

function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") {
      search.set(key, String(value));
    }
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

const digest = (value: unknown): ExecutiveDigestContent | null =>
  value === null || value === undefined ? null : ExecutiveDigestContentSchema.parse(obj(value));

export function createKnowItConversations(transport: ConversationTransport) {
  const messages = (raw: unknown, conversationId: string): ConversationMessage[] =>
    arr(raw).map((entry) => parseMessage(entry, conversationId));

  const context = {
    /** `{items, totalBytes, maxBytes:102400}` — the meter's numbers come from the server. */
    list: (id: string): Promise<ContextPack> =>
      call(transport.rest(`${TURN}/${enc(id)}/context`, "GET"), (raw) =>
        ContextPackSchema.parse(obj(raw)),
      ),

    add: (id: string, item: ContextItemDraft): Promise<ContextPack> =>
      call(
        transport.rest(`${TURN}/${enc(id)}/context`, "POST", {
          content: item.content,
          ...(item.type ? { type: item.type } : {}),
          ...(item.title ? { title: item.title } : {}),
          ...(item.source ? { source: item.source } : {}),
          ...(item.facetTags ? { facetTags: item.facetTags } : {}),
          ...(item.audience ? { audience: item.audience } : {}),
          ...(item.addedBy ? { addedBy: item.addedBy } : {}),
          ...(item.status ? { status: item.status } : {}),
          ...(item.createdById ? { createdById: item.createdById } : {}),
        }),
        (raw) => {
          const body = obj(raw);
          return ContextPackSchema.parse({
            items: [ContextItemSchema.parse(obj(body.item))],
            totalBytes: int(body.totalBytes),
            maxBytes: int(body.maxBytes, 102_400),
          });
        },
      ),

    /** The update body takes NO `createdById` — the server ignores it and kms sent it anyway. */
    update: (id: string, itemId: string, patch: Partial<ContextItemDraft>): Promise<ContextItem> =>
      call(
        transport.rest(`${TURN}/${enc(id)}/context/${enc(itemId)}`, "PUT", {
          ...(patch.type ? { type: patch.type } : {}),
          ...(patch.title !== undefined ? { title: patch.title } : {}),
          ...(patch.content !== undefined ? { content: patch.content } : {}),
          ...(patch.source ? { source: patch.source } : {}),
          ...(patch.facetTags ? { facetTags: patch.facetTags } : {}),
          ...(patch.audience ? { audience: patch.audience } : {}),
          ...(patch.status ? { status: patch.status } : {}),
        }),
        (raw) => ContextItemSchema.parse(obj(obj(raw).item)),
      ),

    /** **204**, and a soft mark: the row stays and still lists with `status:"removed"`. */
    remove: (id: string, itemId: string): Promise<void> =>
      call(transport.rest(`${TURN}/${enc(id)}/context/${enc(itemId)}`, "DELETE"), () => undefined),

    snapshot: (id: string, includedContextItemIds?: string[]): Promise<ContextSnapshot> =>
      call(
        transport.rest(`${TURN}/${enc(id)}/context/snapshot`, "POST", {
          ...(includedContextItemIds ? { includedContextItemIds } : {}),
        }),
        (raw) => ContextSnapshotSchema.parse(obj(raw)),
      ),

    config: (id: string): Promise<ContextConfig> =>
      call(transport.rest(`${TURN}/${enc(id)}/context/config`, "GET"), (raw) =>
        ContextConfigSchema.parse(obj(raw)),
      ),

    setConfig: (
      id: string,
      patch: { status?: string; aggressiveness?: string; updatedById?: string },
    ): Promise<ContextConfig> =>
      call(transport.rest(`${TURN}/${enc(id)}/context/config`, "PUT", { ...patch }), (raw) =>
        ContextConfigSchema.parse(obj(raw)),
      ),

    /**
     * A POST named "suggest" that WRITES: it persists every suggestion as a `context_item` row
     * before answering, so it is a mutation and must invalidate the context key (§D.3 rule 5).
     */
    suggest: (
      id: string,
      options: { aggressiveness?: string; limit?: number } = {},
    ): Promise<ContextItem[]> =>
      call(transport.rest(`${TURN}/${enc(id)}/context/suggest`, "POST", { ...options }), (raw) =>
        arr(obj(raw).suggestions).map((entry) => ContextItemSchema.parse(obj(entry))),
      ),
  };

  const thinkers = {
    list: (id: string): Promise<string[]> =>
      call(transport.rest(`${TURN}/${enc(id)}/thinkers`, "GET"), (raw) =>
        strings(obj(raw).thinkerIds),
      ),

    /** The order IS the answer order — "thinkers respond in this order". */
    set: (id: string, thinkerIds: string[]): Promise<string[]> =>
      call(transport.rest(`${TURN}/${enc(id)}/thinkers`, "PUT", { thinkerIds }), (raw) =>
        strings(obj(raw).thinkerIds),
      ),

    add: (id: string, thinkerId: string): Promise<string[]> =>
      call(transport.rest(`${TURN}/${enc(id)}/thinkers/${enc(thinkerId)}`, "POST"), (raw) =>
        strings(obj(raw).thinkerIds),
      ),

    /**
     * ⚠ W8-3 — this DELETE takes a JSON BODY and defaults to `action:"delete"`, which
     * **hard-deletes every message that thinker authored**. A proxy that strips DELETE bodies
     * silently downgrades `merge` to that default, so the action rides the QUERY STRING too.
     * `messagesAffected` comes back and must be surfaced, never swallowed.
     */
    remove: (
      id: string,
      thinkerId: string,
      disposition: ThinkerDisposition,
    ): Promise<ThinkerRemovalResult> =>
      call(
        transport.rest(
          `${TURN}/${enc(id)}/thinkers/${enc(thinkerId)}${query({
            action: disposition.action,
            ...(disposition.mergeToThinkerId
              ? { mergeToThinkerId: disposition.mergeToThinkerId }
              : {}),
          })}`,
          "DELETE",
          {
            thinkerId,
            action: disposition.action,
            ...(disposition.mergeToThinkerId
              ? { mergeToThinkerId: disposition.mergeToThinkerId }
              : {}),
          },
        ),
        (raw) => {
          const body = obj(raw);
          return {
            thinkerIds: strings(body.thinkerIds),
            messagesAffected: int(body.messagesAffected),
            action: str(body.action) ?? disposition.action,
          };
        },
      ),
  };

  const outcomes = {
    get: (id: string): Promise<Outcomes> =>
      call(transport.rest(`${TURN}/${enc(id)}/outcomes`, "GET"), (raw) =>
        OutcomesSchema.parse(obj(raw)),
      ),

    /** `messageIds` on the way in, `provenance` on the way out — same thing (W8-28). */
    createTruth: (id: string, draft: TruthDraft): Promise<DerivedTruth> =>
      call(transport.rest(`${TURN}/${enc(id)}/truths`, "POST", { ...draft }), (raw) =>
        DerivedTruthSchema.parse(obj(raw)),
      ),

    /** Append-only: a second supersede on the same truth is a 400, by design. */
    supersedeTruth: (
      id: string,
      truthId: string,
      draft: Partial<TruthDraft>,
    ): Promise<{ superseded: DerivedTruth; replacement: DerivedTruth }> =>
      call(
        transport.rest(`${TURN}/${enc(id)}/truths/${enc(truthId)}/supersede`, "POST", {
          ...draft,
        }),
        (raw) => {
          const body = obj(raw);
          return {
            superseded: DerivedTruthSchema.parse(obj(body.superseded)),
            replacement: DerivedTruthSchema.parse(obj(body.replacement)),
          };
        },
      ),

    createDecision: (id: string, draft: DecisionDraft): Promise<Decision> =>
      call(transport.rest(`${TURN}/${enc(id)}/decisions`, "POST", { ...draft }), (raw) =>
        DecisionSchema.parse(obj(raw)),
      ),

    /** Speech act → a proposed truth and/or decision. The promote-to-outcome affordance. */
    react: (
      id: string,
      messageId: string,
      act: { kind?: string; content?: string } = {},
    ): Promise<{ proposed: boolean; derivedTruthId: string | null; decisionId: string | null }> =>
      call(
        transport.rest(`${TURN}/${enc(id)}/messages/${enc(messageId)}/react`, "POST", {
          kind: act.kind ?? "auto",
          ...(act.content ? { content: act.content } : {}),
        }),
        (raw) => {
          const body = obj(raw);
          return {
            proposed: body.proposed === true,
            derivedTruthId: str(body.derivedTruthId),
            decisionId: str(body.decisionId),
          };
        },
      ),

    /** Create returns `{planId, kind}` and there is NO read endpoint — hold on to the id. */
    createPlan: (id: string, draft: PlanDraft): Promise<{ planId: string; kind: string }> =>
      call(transport.rest(`${TURN}/${enc(id)}/plans`, "POST", { ...draft }), (raw) => {
        const body = obj(raw);
        return { planId: str(body.planId) ?? "", kind: str(body.kind) ?? "Task" };
      }),

    bind: (
      id: string,
      decisionId: string,
      draft: BindDraft,
    ): Promise<{ decisionId: string; lifecycle: string }> =>
      call(
        transport.rest(`${TURN}/${enc(id)}/decisions/${enc(decisionId)}/bind`, "POST", {
          planId: draft.planId,
          trigger: draft.trigger,
          ...(draft.autonomyLevel ? { autonomyLevel: draft.autonomyLevel } : {}),
          thinkerInitiated: draft.thinkerInitiated === true,
        }),
        (raw) => {
          const body = obj(raw);
          return {
            decisionId: str(body.decisionId) ?? decisionId,
            lifecycle: str(body.lifecycle) ?? "",
          };
        },
      ),

    /**
     * ⚠ W8-2 — `pause`/`resume`/`cancel` answer **200 unconditionally**, even for a decision
     * that does not exist, and `approve`/`deny` have no "who may approve" check at all. The
     * caller must re-read the decision and compare `lifecycle`; never report success from the
     * status alone.
     */
    control: (
      id: string,
      decisionId: string,
      action: DecisionAction,
    ): Promise<{ decisionId: string; lifecycle: string | null; acknowledged: boolean }> =>
      call(
        transport.rest(`${TURN}/${enc(id)}/decisions/${enc(decisionId)}/${action}`, "POST"),
        (raw) => {
          const body = obj(raw);
          return {
            decisionId: str(body.decisionId) ?? decisionId,
            lifecycle: str(body.lifecycle),
            acknowledged:
              body.paused === true ||
              body.resumed === true ||
              body.cancelled === true ||
              typeof body.lifecycle === "string",
          };
        },
      ),

    runs: (id: string): Promise<BdiRun[]> =>
      call(transport.rest(`${TURN}/${enc(id)}/runs`, "GET"), (raw) =>
        arr(obj(raw).runs).map((entry) => BdiRunSchema.parse(obj(entry))),
      ),

    usage: (
      id: string,
      scope: { decisionId?: string; runId?: string } = {},
    ): Promise<UsageRollup> =>
      call(transport.rest(`${TURN}/${enc(id)}/usage${query({ ...scope })}`, "GET"), (raw) =>
        UsageRollupSchema.parse(obj(raw)),
      ),
  };

  /**
   * Document-level AI settings and the resolved "document thinker".
   *
   * Deliberately here rather than on `documents.*` (where the wave spec sketched it): the whole
   * document-AI surface then lands through ONE namespace object, which is what keeps the four
   * shared adapter files to one insertion point each while three waves build in parallel.
   */
  const documentAi = {
    settings: (documentId: string): Promise<DocumentAiSettings> =>
      call(transport.rest("documents.ai_settings", "POST", { id: documentId }), (raw) =>
        DocumentAiSettingsSchema.parse(obj(raw)),
      ),

    /** `documents.update_ai_settings` EXISTS and no kms surface ever called it (§B.4). */
    setSettings: (
      documentId: string,
      patch: { aiAssistanceEnabled?: boolean; defaultContextItemIds?: string[] },
    ): Promise<DocumentAiSettings> =>
      call(
        transport.rest("documents.update_ai_settings", "POST", { id: documentId, ...patch }),
        (raw) => DocumentAiSettingsSchema.parse(obj(raw)),
      ),

    addThinker: (documentId: string, thinkerId: string): Promise<DocumentAiSettings> =>
      call(transport.rest("documents.add_thinker", "POST", { id: documentId, thinkerId }), (raw) =>
        DocumentAiSettingsSchema.parse(obj(raw)),
      ),

    removeThinker: (documentId: string, thinkerId: string): Promise<DocumentAiSettings> =>
      call(
        transport.rest("documents.remove_thinker", "POST", { id: documentId, thinkerId }),
        (raw) => DocumentAiSettingsSchema.parse(obj(raw)),
      ),

    /** Who answers selection actions and transforms OUTSIDE a conversation, and why. */
    resolved: (input: {
      documentId?: string;
      collectionId?: string;
    }): Promise<DocumentThinkerResolution> =>
      call(transport.rest(`ai-actions/document-thinker${query({ ...input })}`, "GET"), (raw) =>
        DocumentThinkerResolutionSchema.parse(obj(raw)),
      ),

    /** An IDE pin is ALWAYS `scope:"user"` — never collection-wide (§B.4). */
    pin: (draft: {
      thinkerId: string;
      collectionId?: string;
      scope?: "collection" | "user";
      documentId?: string;
      machineId?: string;
      workspacePath?: string;
      displayLabel?: string;
    }): Promise<DocumentThinkerResolution> =>
      call(transport.rest("ai-actions/document-thinker", "PUT", { ...draft }), (raw) =>
        DocumentThinkerResolutionSchema.parse(obj(raw)),
      ),
  };

  return {
    // ── turn face ────────────────────────────────────────────────────────────
    /**
     * The drawer's index. ⚠ W8-8: `limit` is applied AFTER the query with no cursor and no
     * total, so a short page never means "no more" — pass an explicit limit and say what it is.
     */
    forDocument: (
      documentId: string,
      search: ConversationSearch = {},
    ): Promise<ConversationSummary[]> =>
      call(
        transport.rest(
          `${TURN}/document/${enc(documentId)}${query({
            search: search.search,
            resolved: search.resolved,
            priority: search.priority ? priorityValue(search.priority) : undefined,
            limit: search.limit ?? 50,
          })}`,
          "GET",
        ),
        (raw) => arr(raw).map(parseTurnSummary),
      ),

    /** RAW messages — control rows included. The drawer's read (§F.4 decision 2). */
    detail: (id: string): Promise<Conversation> =>
      call(transport.rest(`${TURN}/${enc(id)}`, "GET"), (raw) => parseConversation(raw, "turn")),

    /**
     * **201** with a client-supplied id. S10 depends on minting it: a comment thread's id IS
     * its conversation's id, and there is no join table anywhere in the design.
     */
    start: (draft: StartConversationDraft): Promise<{ id: string }> =>
      call(
        transport.rest(TURN, "POST", {
          ...(draft.id ? { id: draft.id } : {}),
          documentId: draft.documentId,
          title: draft.title,
          ...(draft.initialMessage ? { initialMessage: draft.initialMessage } : {}),
          ...(draft.createdBy ? { createdBy: draft.createdBy } : {}),
          authorType: "human",
          priority: priorityValue(draft.priority ?? "Normal"),
          ...(draft.tags ? { tags: draft.tags } : {}),
          ...(draft.anchors ? { anchors: draft.anchors } : {}),
        }),
        (raw) => ({ id: str(obj(raw).id) ?? draft.id ?? "" }),
      ),

    update: (
      id: string,
      patch: { title?: string; priority?: string; isResolved?: boolean; tags?: string[] },
    ): Promise<void> =>
      call(
        transport.rest(`${TURN}/${enc(id)}`, "PATCH", {
          ...(patch.title !== undefined ? { title: patch.title } : {}),
          ...(patch.priority !== undefined
            ? { priority: priorityValue(patch.priority as "Normal") }
            : {}),
          ...(patch.isResolved !== undefined ? { isResolved: patch.isResolved } : {}),
          ...(patch.tags !== undefined ? { tags: patch.tags } : {}),
        }),
        () => undefined,
      ),

    /** HARD delete on this face — the lifecycle face's `remove` is the soft one. */
    destroy: (id: string): Promise<void> =>
      call(transport.rest(`${TURN}/${enc(id)}`, "DELETE"), () => undefined),

    /** Persist + invoke inline (up to 3 thinkers, synchronously). Prefer `stream`. */
    send: (id: string, message: MessageDraft): Promise<ConversationMessage> =>
      call(
        transport.rest(`${TURN}/${enc(id)}/messages`, "POST", {
          content: message.content,
          author: message.author ?? "",
          authorType: message.authorType ?? "human",
          messageType: message.messageType ?? "text",
          ...(message.metadata ? { metadata: message.metadata } : {}),
          ...(message.targetThinkerIds ? { targetThinkerIds: message.targetThinkerIds } : {}),
        }),
        (raw) => parseMessage(obj(raw), id),
      ),

    /** The product's core call. All 29 event names are handled in `conversationStream.ts`. */
    stream: (
      id: string,
      message: MessageDraft,
      onEvent: ConversationStreamHandler,
      signal?: AbortSignal,
    ): Promise<void> =>
      transport.stream(
        `${TURN}/${enc(id)}/messages/stream`,
        {
          content: message.content,
          author: message.author ?? "",
          authorType: message.authorType ?? "human",
          messageType: message.messageType ?? "text",
          ...(message.metadata ? { metadata: message.metadata } : {}),
          ...(message.targetThinkerIds ? { targetThinkerIds: message.targetThinkerIds } : {}),
        },
        onEvent,
        signal,
      ),

    /**
     * ⚠ There is no conversation id in this route, and `revisionId` is **null on the no-op
     * path** — an unchanged edit is indistinguishable from a failure except by the stale lists.
     */
    revise: (messageId: string, patch: RevisePatch): Promise<ReviseResult> =>
      call(
        transport.rest(`${TURN}/messages/${enc(messageId)}/revise`, "POST", {
          content: patch.content,
          ...(patch.editedById ? { editedById: patch.editedById } : {}),
          ...(patch.editedByName ? { editedByName: patch.editedByName } : {}),
          ...(patch.reason ? { reason: patch.reason } : {}),
          markDownstreamStale: patch.markDownstreamStale !== false,
        }),
        (raw) => {
          const body = obj(raw);
          return {
            revisionId: str(body.revisionId),
            revision: int(body.revision),
            staleMessageIds: strings(body.staleMessageIds),
            staleArtifactIds: strings(body.staleArtifactIds),
          };
        },
      ),

    /** ⚠ The id rides the QUERY STRING — the only POST in the set that does. */
    regenerate: (id: string, fromMessageId?: string): Promise<RegenerateResult> =>
      call(
        transport.rest(`${TURN}/${enc(id)}/regenerate${query({ fromMessageId })}`, "POST", {}),
        (raw) => {
          const body = obj(raw);
          return {
            conversationId: str(body.conversationId) ?? id,
            edContent: digest(body.edContent),
            clearedMessageIds: strings(body.clearedMessageIds),
            updatedArtifactIds: strings(body.updatedArtifactIds),
          };
        },
      ),

    commit: (
      id: string,
      options: { title?: string; requestedById?: string; requestedByName?: string } = {},
    ): Promise<CommitResult> =>
      call(
        transport.rest(`${TURN}/${enc(id)}/commit`, "POST", {
          ...options,
          includeModeratorSynthesis: true,
        }),
        (raw) => {
          const body = obj(raw);
          return {
            conversationId: str(body.conversationId) ?? id,
            artifactId: str(body.artifactId),
            status: str(body.status) ?? "committed",
            committedAt: str(body.committedAt),
            title: str(body.title),
            edContent: digest(body.edContent),
            moderatorSynthesis: str(body.moderatorSynthesis),
          };
        },
      ),

    reopen: (
      id: string,
      who: { requestedById?: string; requestedByName?: string } = {},
    ): Promise<{ status: string }> =>
      call(transport.rest(`${TURN}/${enc(id)}/reopen`, "POST", { ...who }), (raw) => ({
        status: str(obj(raw).status) ?? "active",
      })),

    /**
     * ⚠ W8-17 — `includeFullConversation:true` filters on `AuthorType == "user"`, a value
     * nothing writes (`"human"` is), so it returns AI turns only. Kept false.
     */
    flatten: (
      id: string,
      options: { title?: string; wrapWithHeading?: boolean } = {},
    ): Promise<FlattenResult> =>
      call(
        transport.rest(`${TURN}/${enc(id)}/flatten`, "POST", {
          ...options,
          includeFullConversation: false,
        }),
        (raw) => {
          const body = obj(raw);
          return {
            conversationId: str(body.conversationId) ?? id,
            content: str(body.content) ?? "",
            format: str(body.format) ?? "markdown",
            moderatorSynthesis: str(body.moderatorSynthesis),
            sourceMessageIds: strings(body.sourceMessageIds),
          };
        },
      ),

    /**
     * W8-15 — the composer's `promptType`/`outputFormat`/`thoughtLevel`/facet are COLLECTED
     * and never sent by kms. This endpoint is live and takes all of them; shipping dead knobs
     * is what created that defect, so slate sends them here.
     */
    prompt: (id: string, draft: PromptRunDraft): Promise<ConversationMessage> =>
      call(
        transport.rest(`${TURN}/${enc(id)}/prompts`, "POST", {
          content: draft.content,
          ...(draft.promptType ? { promptType: draft.promptType } : {}),
          ...(draft.outputFormat ? { outputFormat: draft.outputFormat } : {}),
          ...(draft.thoughtLevel ? { thoughtLevel: draft.thoughtLevel } : {}),
          ...(draft.facetTags ? { facetTags: draft.facetTags } : {}),
          broadcast: draft.broadcast ?? (draft.targetThinkerIds ?? []).length === 0,
          ...(draft.targetThinkerIds ? { targetThinkerIds: draft.targetThinkerIds } : {}),
          ...(draft.contextItemIds ? { contextItemIds: draft.contextItemIds } : {}),
          author: draft.author ?? "",
          authorType: "human",
        }),
        (raw) => parseMessage(obj(raw), id),
      ),

    stats: (documentId: string): Promise<ConversationStats> =>
      call(transport.rest(`${TURN}/document/${enc(documentId)}/stats`, "GET"), (raw) => {
        const body = obj(raw);
        const breakdown = obj(body.priorityBreakdown);
        return {
          totalThoughts: int(body.totalThoughts),
          resolvedThoughts: int(body.resolvedThoughts),
          unresolvedThoughts: int(body.unresolvedThoughts),
          totalMessages: int(body.totalMessages),
          priorityBreakdown: Object.fromEntries(
            Object.entries(breakdown).map(([key, value]) => [key, int(value)]),
          ),
        };
      }),

    /** An ABSOLUTE route, not under the thoughts prefix. Newest first, hard `.Take(500)`. */
    sourceTraces: (documentId: string): Promise<SourceTrace[]> =>
      call(transport.rest(`documents/${enc(documentId)}/source-traces`, "GET"), (raw) =>
        arr(raw).map((entry) => SourceTraceSchema.parse(obj(entry))),
      ),

    // ── lifecycle face ───────────────────────────────────────────────────────
    /** `processingCount` is hardcoded 0 server-side — never render it as "nothing running". */
    overview: (limit = 20): Promise<ConversationsOverview> =>
      call(transport.rest(`${LIFECYCLE}/summary${query({ limit })}`, "GET"), (raw) => {
        const body = obj(raw);
        return {
          totalCount: int(body.totalCount),
          activeCount: int(body.activeCount),
          processingCount: int(body.processingCount),
          recentConversations: arr(body.recentConversations).map(parseLifecycleSummary),
          byDocument: arr(body.byDocument).map((entry) => {
            const row = obj(entry);
            return {
              documentId: str(row.documentId) ?? "",
              title: str(row.title) ?? "",
              conversationCount: int(row.conversationCount),
              lastUpdated: str(row.lastUpdated),
            };
          }),
        };
      }),

    /** Deleted ≤30 days, own rows only. Past the window `restore` answers **409**. */
    recentlyDeleted: (): Promise<ConversationSummary[]> =>
      call(transport.rest(`${LIFECYCLE}/recently-deleted`, "GET"), (raw) =>
        arr(raw).map(parseLifecycleSummary),
      ),

    rename: (id: string, title: string): Promise<{ id: string; title: string }> =>
      call(transport.rest(`${LIFECYCLE}/${enc(id)}`, "PATCH", { title }), (raw) => {
        const body = obj(raw);
        return { id: str(body.id) ?? id, title: str(body.title) ?? title };
      }),

    /** SOFT delete, restorable for 30 days. */
    remove: (id: string): Promise<{ id: string; deletedAt: string | null }> =>
      call(transport.rest(`${LIFECYCLE}/${enc(id)}`, "DELETE"), (raw) => {
        const body = obj(raw);
        return { id: str(body.id) ?? id, deletedAt: str(body.deletedAt) };
      }),

    restore: (id: string): Promise<{ id: string }> =>
      call(transport.rest(`${LIFECYCLE}/${enc(id)}/restore`, "POST"), (raw) => ({
        id: str(obj(raw).id) ?? id,
      })),

    /**
     * Sets `isResolved`. ⚠ Un-archiving lives on the OTHER controller:
     * `PATCH .../thoughts/{id} {isResolved:false}` — this face has no verb for it (§A.2).
     */
    archive: (id: string): Promise<{ id: string; status: string }> =>
      call(transport.rest(`${LIFECYCLE}/${enc(id)}/archive`, "POST"), (raw) => {
        const body = obj(raw);
        return { id: str(body.id) ?? id, status: str(body.status) ?? "Archived" };
      }),

    /** The sources are KEPT; a new conversation is created. Fewer than two is a 400. */
    merge: (
      sourceConversationIds: string[],
      title?: string,
      regenerateSummary = true,
    ): Promise<Conversation> =>
      call(
        transport.rest(`${LIFECYCLE}/merge`, "POST", {
          sourceConversationIds,
          ...(title ? { title } : {}),
          regenerateSummary,
        }),
        (raw) => parseConversation(raw, "lifecycle"),
      ),

    /** `asExport:true` makes a NEW deliverable and must NOT overwrite the companion link. */
    transform: (
      id: string,
      kind: "Summarize" | "Simplify",
      asExport = false,
    ): Promise<CompanionResult> =>
      call(
        transport.rest(`${LIFECYCLE}/${enc(id)}/transform`, "POST", { kind, asExport }),
        (raw) => {
          const body = obj(raw);
          return {
            knowItDocumentId: str(body.knowItDocumentId),
            markdown: str(body.markdown) ?? "",
          };
        },
      ),

    regenerateSummary: (id: string): Promise<CompanionResult> =>
      call(transport.rest(`${LIFECYCLE}/${enc(id)}/summary/regenerate`, "POST"), (raw) => {
        const body = obj(raw);
        return { knowItDocumentId: str(body.knowItDocumentId), markdown: str(body.markdown) ?? "" };
      }),

    /** Persist only — this face NEVER invokes a thinker. That is the turn face's job. */
    appendMessage: (id: string, message: MessageDraft): Promise<ConversationMessage> =>
      call(
        transport.rest(`${LIFECYCLE}/${enc(id)}/messages`, "POST", {
          content: message.content,
          ...(message.metadata?.thinkerId ? { thinkerId: message.metadata.thinkerId } : {}),
          isSynthesis: message.metadata?.isSynthesis === true,
        }),
        (raw) => parseMessage(obj(raw), id),
      ),

    editMessage: (id: string, messageId: string, content: string): Promise<ConversationMessage> =>
      call(
        transport.rest(`${LIFECYCLE}/${enc(id)}/messages/${enc(messageId)}`, "PUT", { content }),
        (raw) => parseMessage(obj(raw), id),
      ),

    /** Drops every message AFTER the anchor into `messageType:"dropped"` — reversible. */
    rethink: (id: string, messageId: string, updatedContent?: string): Promise<RethinkResult> =>
      call(
        transport.rest(`${LIFECYCLE}/${enc(id)}/messages/${enc(messageId)}/rethink`, "POST", {
          ...(updatedContent === undefined ? {} : { updatedContent }),
        }),
        (raw) => {
          const body = obj(raw);
          return {
            anchorContent: str(body.anchorContent) ?? "",
            droppedCount: int(body.droppedCount),
            droppedMessages: messages(body.droppedMessages, id),
          };
        },
      ),

    restoreDropped: (id: string, messageIds: string[]): Promise<{ restoredCount: number }> =>
      call(
        transport.rest(`${LIFECYCLE}/${enc(id)}/restore-dropped`, "POST", {
          messages: messageIds.map((messageId) => ({ id: messageId })),
        }),
        (raw) => ({ restoredCount: int(obj(raw).restoredCount) }),
      ),

    context,
    thinkers,
    outcomes,
    documentAi,
  };
}

export type KnowItConversations = ReturnType<typeof createKnowItConversations>;
